import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';

export const OrgChartEditModal = ({ open, onOpenChange, node, allNodes, onSave, mode = 'edit' }) => {
  const [puesto, setPuesto] = useState('');
  const [nombre, setNombre] = useState('');
  const [level, setLevel] = useState('0');
  const [parentId, setParentId] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (node && mode === 'edit') {
      setPuesto(node.puesto || '');
      setNombre(node.nombre || '');
      setLevel(String(node.level ?? 0));
      setParentId(node.parent_id || '');
      setDescription(node.description || '');
    } else if (mode === 'create') {
      setPuesto(''); setNombre(''); setLevel('0'); setParentId(''); setDescription('');
    }
  }, [node, mode, open]);

  const handleSave = async () => {
    if (!puesto.trim()) { toast.error('El puesto es obligatorio'); return; }
    if (!nombre.trim()) { toast.error('El nombre es obligatorio'); return; }
    setSaving(true);
    try {
      await onSave({
        puesto: puesto.trim(),
        nombre: nombre.trim(),
        level: parseInt(level),
        parent_id: parentId || null,
        description: description.trim(),
      });
      onOpenChange(false);
    } catch (e) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const parentOptions = (allNodes || []).filter(n => !node || n.node_id !== node?.node_id);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="orgchart-edit-modal">
        <DialogHeader>
          <DialogTitle>{mode === 'edit' ? 'Editar Posicion' : 'Nueva Posicion'}</DialogTitle>
          <DialogDescription>
            {mode === 'edit' ? 'Modifica los datos de esta posicion.' : 'Completa los datos de la nueva posicion.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label>Puesto *</Label>
            <Input value={puesto} onChange={e => setPuesto(e.target.value)} placeholder="Ej: Gerente de Ventas" data-testid="orgchart-edit-puesto" />
          </div>
          <div>
            <Label>Nombre del ocupante *</Label>
            <Input value={nombre} onChange={e => setNombre(e.target.value)} placeholder="Ej: Juan Perez" data-testid="orgchart-edit-nombre" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Nivel Jerarquico</Label>
              <Select value={level} onValueChange={setLevel}>
                <SelectTrigger data-testid="orgchart-edit-level"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[0,1,2,3,4,5,6].map(l => (
                    <SelectItem key={l} value={String(l)}>Nivel {l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Jefe Directo</Label>
              <Select value={parentId || '_none'} onValueChange={v => setParentId(v === '_none' ? '' : v)}>
                <SelectTrigger data-testid="orgchart-edit-parent"><SelectValue placeholder="Ninguno" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">Ninguno (raiz)</SelectItem>
                  {parentOptions.map(n => (
                    <SelectItem key={n.node_id} value={n.node_id}>{n.puesto} - {n.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Descripcion del puesto</Label>
            <Textarea value={description} onChange={e => setDescription(e.target.value)} rows={3} placeholder="Descripcion de funciones..." data-testid="orgchart-edit-description" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="orgchart-edit-save">
            {saving ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const OrgChartDeleteConfirm = ({ open, onOpenChange, node, childrenCount, onConfirm }) => {
  const [deleting, setDeleting] = useState(false);
  const handleDelete = async () => {
    setDeleting(true);
    try {
      await onConfirm();
      onOpenChange(false);
    } catch {
      toast.error('Error al eliminar');
    } finally {
      setDeleting(false);
    }
  };
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="orgchart-delete-confirm">
        <AlertDialogHeader>
          <AlertDialogTitle>Eliminar posicion</AlertDialogTitle>
          <AlertDialogDescription>
            Estas seguro de eliminar <strong>{node?.puesto}</strong> ({node?.nombre})?
            {childrenCount > 0 && (
              <span className="block mt-2 text-amber-600 font-medium">
                Este nodo tiene {childrenCount} subordinado(s) directo(s). Quedaran sin jefe asignado.
              </span>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancelar</AlertDialogCancel>
          <AlertDialogAction onClick={handleDelete} disabled={deleting} className="bg-red-600 hover:bg-red-700">
            {deleting ? 'Eliminando...' : 'Eliminar'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
